<template>
    <div>
        <div class="pb-4">
            <div class="flex items-center gap-2">
                <div class="">
                    <div class="text-xs uppercase text-gray-500">
                        {{
                            localizer.translate(
                                TextTokenEnum.BookingForm_Currency,
                            )
                        }}
                    </div>
                    <div>
                        <SelectInput
                            :useBookingFormThemeCssVariables="true"
                            :name="
                                RequestBodyKeyEnum.ScheduleMeeting_ServicePriceId
                            "
                            :value="selectedServicePrice?.id.value"
                            @update:modelValue="
                                handleServicePriceSelectionChange($event)
                            "
                        >
                            <template
                                v-for="servicePrice in serviceAggregate.servicePrices"
                                :key="servicePrice.id.value"
                            >
                                <option :value="servicePrice.id.value">
                                    {{ servicePrice.price.currency }}
                                </option>
                            </template>
                        </SelectInput>
                    </div>
                </div>
                <div>
                    <div class="text-xs uppercase text-gray-500">
                        {{
                            localizer.translate(TextTokenEnum.BookingForm_Total)
                        }}
                    </div>
                    <div
                        v-if="totalPrice"
                        class="my-1 py-px"
                    >
                        <span class="text-2xl py-2 font-semibold text-dark">
                            {{ totalPrice.getValueAsDecimal() }}
                        </span>
                    </div>
                </div>
            </div>
        </div>
        <div v-if="customerPaymentMethods.length > 0">
            <div>
                <div class="space-y-1">
                    <RadioInput
                        :useBookingFormThemeCssVariables="true"
                        label="Use saved cards"
                        name="selectedPaymentMethodMode"
                        :checked="customerPaymentMethods.length > 0"
                        :value="PaymentMethodModeEnum.SavedPaymentMethod"
                        v-model="selectedPaymentMethodMode"
                    />
                    <RadioInput
                        :useBookingFormThemeCssVariables="true"
                        label="Enter new card"
                        name="selectedPaymentMethodMode"
                        :checked="customerPaymentMethods.length <= 0"
                        :value="PaymentMethodModeEnum.NewPaymentMethod"
                        v-model="selectedPaymentMethodMode"
                    />
                </div>
            </div>
        </div>
        <div class="mt-8">
            <div
                v-show="
                    selectedPaymentMethodMode ===
                    PaymentMethodModeEnum.SavedPaymentMethod
                "
            >
                <div class="space-y-1">
                    <template
                        v-for="customerPaymentMethod in customerPaymentMethods"
                        :key="customerPaymentMethod.id.value"
                    >
                        <Selectable
                            v-slot="selectable"
                            v-model="selectedCustomerPaymentMethod"
                            :value="customerPaymentMethod.id.value"
                        >
                            <StripeCustomerPaymentMethodCard
                                :customerPaymentMethod="customerPaymentMethod"
                                :isSelected="selectable.active"
                            />
                        </Selectable>
                    </template>
                    <input
                        type="hidden"
                        name="customerPaymentMethodId"
                        :value="selectedCustomerPaymentMethod"
                    />
                </div>
            </div>
            <div
                v-show="
                    selectedPaymentMethodMode ===
                    PaymentMethodModeEnum.NewPaymentMethod
                "
            >
                <div>
                    <div
                        id="payment-element"
                        class="mt-2"
                    ></div>
                </div>
                <div
                    v-if="hasSavePaymentMethodFeatureFlag"
                    class="mt-4"
                >
                    <FormControl
                        :label="
                            localizer.translate(
                                TextTokenEnum.BookingForm_SavePaymentMethod,
                            )
                        "
                    >
                        <CheckboxInput
                            :useBookingFormThemeCssVariables="true"
                            :label="
                                localizer.translate(
                                    TextTokenEnum.BookingForm_SaveThisPaymentMethodForFutureUsage,
                                )
                            "
                            name="savePaymentMethod"
                            :checked="draftMeeting.savePaymentMethod ?? false"
                        />
                    </FormControl>
                </div>
            </div>
        </div>

        <template
            v-if="
                serviceAggregate.service.isPaymentRequired &&
                serviceAggregate.integration?.provider ===
                    IntegrationProviderEnum.StripeConnect
            "
        >
            <input
                class="optional"
                type="text"
                :name="
                    RequestBodyKeyEnum.ScheduleMeeting_Stripe_ConfirmationToken
                "
                :value="confirmationToken"
            />
        </template>

        <div>
            <div
                v-if="turnstileEnabled"
                class="my-4 flex justify-end"
            >
                <div
                    class="cf-turnstile"
                    :data-sitekey="turnstileSiteKey"
                ></div>
            </div>
            <div class="flex justify-end">
                <Button
                    @click="submitPlaceOrderRequest()"
                    :loading="isProcessingPayment"
                    :disabled="isConfirmButtonDisabled"
                    :useBookingFormThemeCssVariables="true"
                    iconName="check"
                >
                    {{
                        localizer.translate(
                            TextTokenEnum.BookingForm_BookMeeting,
                        )
                    }}
                </Button>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import {
        computed,
        nextTick,
        onMounted,
        ref,
        watch,
        type PropType,
    } from 'vue'
    import type {
        Stripe,
        StripeElementLocale,
        StripeElements,
        StripePaymentElement,
    } from '@stripe/stripe-js'
    import Company from '@/App/Company/Entity/Company'
    import CustomerPaymentMethod from '@/App/CustomerPaymentMethod/Entity/CustomerPaymentMethod'
    import DraftMeeting from '@/App/Booking/Draft/Entity/DraftMeeting'
    import ServiceAggregate from '@/App/Service/Entity/ServiceAggregate'
    import ServicePrice from '@/App/Service/Entity/ServicePrice'
    import Button from '@/Shared/Component/Button.vue'
    import CheckboxInput from '@/Shared/Component/Forms/CheckboxInput.vue'
    import RadioInput from '@/Shared/Component/Forms/RadioInput.vue'
    import SelectInput from '@/Shared/Component/Forms/SelectInput.vue'
    import Selectable from '@/Shared/Component/Selectable.vue'
    import FormControl from '@/Shared/Component/Forms/FormControl.vue'
    import useMetaAttribute from '@/Shared/Hook/useMetaAttribute'
    import {
        IntegrationProviderEnum,
        PaymentMethodModeEnum,
    } from '@/Shared/AutoGenerated/Main'
    import {
        type ConfirmBookMeetingCanScheduleMeetingCallback,
        type ConfirmBookMeetingOnErrorCallbackParams,
        type ConfirmBookMeetingOnScheduleMeetingRequested,
        type ConfirmBookMeetingOnStartCallbackParams,
    } from '@/Shared/AutoGenerated/Types'
    import StripeCustomerPaymentMethodCard from '@/BookingForm/BookingForm/Component/Confirmation/StripeCustomerPaymentMethodCard.vue'
    import type LocalizerService from '@/Shared/Service/Localization/LocalizerService'
    import { RequestBodyKeyEnum } from '@/Shared/AutoGenerated/RequestFieldEnum'
    import { TextTokenEnum } from '@/Shared/AutoGenerated/LocalizationEnum'

    const emits = defineEmits<{
        onStart: [ConfirmBookMeetingOnStartCallbackParams]
        onError: [ConfirmBookMeetingOnErrorCallbackParams]
        onScheduleMeetingRequested: [
            ConfirmBookMeetingOnScheduleMeetingRequested,
        ]
    }>()

    const props = defineProps({
        company: {
            type: Object as PropType<Company>,
            required: true,
        },
        draftMeeting: {
            type: Object as PropType<DraftMeeting>,
            required: true,
        },
        serviceAggregate: {
            type: Object as PropType<ServiceAggregate>,
            required: true,
        },
        customerPaymentMethods: {
            type: Array as PropType<CustomerPaymentMethod[]>,
            required: true,
        },
        turnstileEnabled: {
            type: Boolean,
            required: true,
        },
        turnstileSiteKey: {
            type: String,
            required: true,
        },
        stripePublicKey: {
            type: String,
            default: null,
        },
        hasSavePaymentMethodFeatureFlag: {
            type: Boolean,
            default: null,
        },
        isProcessingPayment: {
            type: Boolean,
            required: true,
        },
        numberOfRecurringMeetingsToSchedule: {
            type: Number,
            required: true,
        },
        numberOfRequestedSeats: {
            type: Number,
            required: true,
        },
        localizer: {
            type: Object as PropType<LocalizerService>,
            required: true,
        },
        canScheduleMeeting: {
            type: Function as PropType<ConfirmBookMeetingCanScheduleMeetingCallback>,
            required: true,
        },
    })

    /**
     * Payment method selecttion (saved or new)
     */
    const hasSavedPaymentMethods = props.customerPaymentMethods.length > 0
    const selectedPaymentMethodMode = ref<PaymentMethodModeEnum>(
        hasSavedPaymentMethods
            ? PaymentMethodModeEnum.SavedPaymentMethod
            : PaymentMethodModeEnum.NewPaymentMethod,
    )
    const selectedCustomerPaymentMethod = ref<string | undefined>(undefined)

    /**
     * Service price
     */
    const savedServicePrice = props.draftMeeting.servicePriceId
        ? props.serviceAggregate.getServicePriceById(
              props.draftMeeting.servicePriceId.value,
          )
        : null
    const selectedServicePrice = ref<ServicePrice | null>(
        savedServicePrice ??
            props.serviceAggregate.getDefaultServicePrice() ??
            null,
    )
    const totalPrice = computed(() => {
        if (!selectedServicePrice.value) {
            return null
        }

        return selectedServicePrice.value.calculateTotalPrice(
            props.numberOfRecurringMeetingsToSchedule,
            props.numberOfRequestedSeats,
        )
    })

    /**
     * Handles updates to the amount/currency, to ensure the element is in sync
     */
    const stripePaymentElementAmount = computed(() => totalPrice.value?.value)
    const stripePaymentElementCurrency = computed(() =>
        totalPrice.value?.currency.toLowerCase(),
    )
    const isConfirmButtonDisabled = computed(() => {
        // Saved cards do not depend on the Stripe Payment Element state.
        if (
            selectedPaymentMethodMode.value !==
            PaymentMethodModeEnum.NewPaymentMethod
        ) {
            return false
        }

        return (
            !isStripePaymentElementReady.value ||
            pendingStripeElementsUpdates.value > 0
        )
    })

    const handleServicePriceSelectionChange = (
        servicePriceId: string | null,
    ) => {
        if (!servicePriceId) {
            throw new Error('Service price should not be null', {
                cause: servicePriceId,
            })
        }

        selectedServicePrice.value = props.serviceAggregate.getServicePriceById(
            parseInt(servicePriceId),
        )
    }

    /**
     * Place order request
     */
    const submitPlaceOrderRequest = () => {
        if (!props.canScheduleMeeting()) {
            return
        }

        emits('onStart', {})

        if (
            props.serviceAggregate.service.isPaymentRequired &&
            selectedPaymentMethodMode.value ===
                PaymentMethodModeEnum.NewPaymentMethod
        ) {
            placeOrderWithNewPaymentMethod()

            return
        }

        if (
            props.serviceAggregate.service.isPaymentRequired &&
            selectedPaymentMethodMode.value ===
                PaymentMethodModeEnum.SavedPaymentMethod
        ) {
            placeOrderWithSavedPaymentMethod()

            return
        }

        throw new Error('Unhandled order confirmation flow', {
            cause: props.serviceAggregate.service.id.value,
        })
    }

    const placeOrderWithNewPaymentMethod = () => {
        // If payment is required, then integration MUST be set
        if (!props.serviceAggregate.integration) {
            emits('onError', {
                message:
                    'Error trying to confirm meeting, please refresh the page and try again (code 1).',
            })

            throw new Error('Integration should not be null', {
                cause: props.serviceAggregate,
            })
        }

        // Setup stripe payments
        if (
            props.serviceAggregate.integration.provider ===
            IntegrationProviderEnum.StripeConnect
        ) {
            startStripePayment()

            return
        }

        emits('onError', {
            message:
                'Error trying to confirm meeting, please refresh the page and try again (code 2).',
        })

        // We should never get here
        throw new Error(
            `Unhandled payment provider '${props.serviceAggregate.integration.provider ?? 'Unknow'}'`,
            { cause: props.serviceAggregate },
        )
    }

    const placeOrderWithSavedPaymentMethod = () => {
        throw new Error('TODO')
    }

    /**
     * Lifecycle hooks
     */
    onMounted(() => {
        setupPaymentProcessing()
    })

    const setupPaymentProcessing = () => {
        if (!props.serviceAggregate.service.isPaymentRequired) {
            throw new Error('Payment is not required.')
        }

        if (!props.serviceAggregate.integration) {
            throw new Error(
                'Payment should be collected by there is no integration',
                { cause: props.serviceAggregate },
            )
        }

        if (
            props.serviceAggregate.integration.provider !==
            IntegrationProviderEnum.StripeConnect
        ) {
            throw new Error(
                `Unhandled payment provider '${props.serviceAggregate.integration.provider}'`,
            )
        }

        setupStripeElements()
    }

    /**
     * Stripe Elements
     */
    let stripe: Stripe | null = null
    let elements: StripeElements | undefined = undefined
    let paymentElement: StripePaymentElement | undefined = undefined

    // Stripe reports async Elements updates through the "update-end" event.
    const pendingStripeElementsUpdates = ref<number>(0)
    const isStripePaymentElementReady = ref<boolean>(false)

    const setupStripeElements = () => {
        if (!props.serviceAggregate.integration?.externalId) {
            throw new Error('Integration is missing external ID.')
        }

        try {
            if (!window.Stripe) {
                throw new Error('Stripe SDK not found.')
            }

            stripe = window.Stripe(props.stripePublicKey, {
                stripeAccount: props.serviceAggregate.integration.externalId,
                locale: props.localizer.getShortStripeLocale() as StripeElementLocale,
            })

            const amount = stripePaymentElementAmount.value
            const currency = stripePaymentElementCurrency.value

            if (amount === undefined || !currency) {
                throw new Error(
                    'Stripe payment amount and currency are required.',
                )
            }

            elements = stripe.elements({
                mode: 'payment',
                amount: amount,
                currency: currency,
                payment_method_types: ['card'],
                locale: props.localizer.getShortStripeLocale() as StripeElementLocale,
                appearance: {
                    variables: {
                        colorPrimary:
                            useMetaAttribute().getBookingFormThemeMainBackgroundColor(),
                        borderRadius: '0.5rem',
                    },
                },
            })

            paymentElement = elements.create('payment', {
                wallets: {
                    googlePay: 'never',
                    applePay: 'never',
                },
            })

            elements.on('update-end', handleStripeElementsUpdateEnd)
            paymentElement.mount('#payment-element')

            isStripePaymentElementReady.value = true
        } catch (error: unknown) {
            emits('onError', {
                message:
                    'Error trying to confirm meeting, please refresh the page and try again (code 3).',
            })

            throw new Error('stripe error', { cause: error })
        }
    }

    const handleStripeElementsUpdateEnd = () => {
        // Keep the button disabled until every queued update has settled.
        if (pendingStripeElementsUpdates.value === 0) {
            return
        }

        pendingStripeElementsUpdates.value -= 1
    }

    watch(
        [stripePaymentElementAmount, stripePaymentElementCurrency],
        ([amount, currency]) => {
            if (!elements || amount === undefined || !currency) {
                return
            }

            // Amount/currency updates can lag on slow networks.
            pendingStripeElementsUpdates.value += 1

            elements.update({
                amount: amount,
                currency: currency,
            })
        },
    )

    /**
     * Confirmation token
     */
    const confirmationToken = ref<string | null>(null)

    const startStripePayment = () => {
        // Prevent confirming with stale Elements amount/currency.
        if (isConfirmButtonDisabled.value) {
            return
        }

        elements
            ?.submit()
            ?.then((response) => {
                if (response.error) {
                    emits('onError', {
                        message:
                            'Error trying to confirm meeting, please refresh the page and try again (code 4).',
                    })

                    return
                }

                if (!elements) {
                    throw new Error('Stripe elements should not be null')
                }

                stripe
                    ?.createConfirmationToken({
                        elements: elements,
                    })
                    .then((response) => {
                        if (response.error) {
                            // Stripe's card input will render the error by itself, and setting message to null will prevent the page from scrolling to the top
                            emits('onError', { message: null })

                            return
                        }

                        confirmationToken.value = response.confirmationToken.id

                        // Let the hidden token input update before the parent reads the form.
                        nextTick(() => {
                            requestScheduleMeeting()
                        })
                    })
                    .catch((error: any) => {
                        emits('onError', {
                            message:
                                'Error trying to confirm meeting, please refresh the page and try again (code 5).',
                        })
                    })
            })
            .catch((error: any) => {
                emits('onError', {
                    message:
                        'Error trying to confirm meeting, please refresh the page and try again (code 7).',
                })

                throw new Error('stripe error', { cause: error })
            })
    }

    const requestScheduleMeeting = () => {
        emits('onScheduleMeetingRequested', {})
    }
</script>
